import { eligibleLineupPool, lineupContractFor } from "./lineupGameEligibility";

const normalize = (sport) => String(sport || "").trim().toUpperCase();
const PAST_EVENT_REASONS = new Set(["EVENT_ALREADY_STARTED", "EVENT_STATUS_BLOCKED"]);

export function lineupEmptyState({ sport, allRows = [], selectedRows = allRows, now = new Date(), dataUnavailable = false } = {}) {
  const normalizedSport = normalize(sport);
  if (dataUnavailable) return `${normalizedSport} data is unavailable.`;
  const allPool = eligibleLineupPool(allRows, { sport: normalizedSport, now });
  if (!allPool.eligible.length && allPool.rejected.length > 0 && allPool.rejected.every(({ decision }) => PAST_EVENT_REASONS.has(decision.reason))) {
    return `${normalizedSport} has no upcoming eligible props.`;
  }
  const selectedPool = eligibleLineupPool(selectedRows, { sport: normalizedSport, now });
  if (allPool.eligible.length > 0 && !selectedPool.eligible.length) {
    return `${normalizedSport} has no valid props for the selected lineup settings.`;
  }
  return `${normalizedSport} has no valid props for the selected lineup settings.`;
}

export function lineupCapability({ sport, availableSports = [], rows = [], now = new Date() } = {}) {
  const normalizedSport = normalize(sport);
  const available = new Set((availableSports || []).map(normalize));
  if (!normalizedSport) return { allowed: false, sport: "", reason: "Select a sport first." };
  if (!available.has(normalizedSport)) {
    return { allowed: false, sport: normalizedSport, reason: `${normalizedSport} is unavailable or not included in your access.` };
  }
  if (!lineupContractFor(normalizedSport)) {
    return { allowed: false, sport: normalizedSport, reason: `Lineup Generator is not available for ${normalizedSport}.` };
  }
  const pool = eligibleLineupPool(rows, { sport: normalizedSport, now });
  if (!pool.eligible.length) {
    return { allowed: true, hasEligiblePool: false, sport: normalizedSport, reason: lineupEmptyState({ sport: normalizedSport, allRows: rows, now }), pool };
  }
  return { allowed: true, hasEligiblePool: true, sport: normalizedSport, reason: "", pool };
}

export function advancedLineupSports({ availableSports = [], rowsBySport = {}, now = new Date() } = {}) {
  return [...new Set((availableSports || []).map(normalize).filter(Boolean))].filter((sport) => {
    const capability = lineupCapability({
    sport,
    availableSports,
    rows: rowsBySport?.[sport],
    now,
    });
    return capability.allowed && capability.hasEligiblePool;
  });
}

export function lineupEntryCapability({ activeSport, availableSports = [], rowsBySport = {}, now = new Date() } = {}) {
  const basic = lineupCapability({ sport: activeSport, availableSports, rows: rowsBySport?.[normalize(activeSport)], now });
  const advancedSports = advancedLineupSports({ availableSports, rowsBySport, now });
  return {
    basic,
    advancedSports,
    visible: (availableSports || []).map(normalize).filter(Boolean).length > 0,
  };
}
